import React from 'react';

/**
 * The list kit's shared vocabulary: design tokens, the traced icon set, the
 * small presentational pieces (select box, pills, chips, bottom sheet), the
 * column / filter / action contracts and the built-in cell renderers.
 *
 * Nothing in this file knows about any particular entity. Screen-specific
 * helpers (price lookups, product type labels, ...) live next to the screen
 * that owns them — see src/page/Item/itemsListShared.tsx.
 */

/**
 * The repo's i18n pattern: look the English string up in the host-provided
 * dictionary, fall back to the English literal when the key is missing (the
 * locale files live in the host shell dev-core-web, not here).
 */
export const t = (language: any, key: string, fallback: string): string =>
  language?.[key] ?? fallback;

/* ------------------------------------------------------------------ */
/* Design tokens                                                       */
/* ------------------------------------------------------------------ */

export const TEAL = '#0F7679';
export const TEAL_DARK = '#0D5759';
export const BORDER_SOFT = '#EBF0F0';
export const BORDER = '#DBE4E4';
export const BORDER_STRONG = '#C9D7D8';
export const TEXT = '#222525';
export const TEXT_MUTED = '#4A5353';
export const TEXT_DIM = '#6A7878';
export const TEXT_FAINT = '#898989';
export const HOVER = '#EBF9F9';
export const DESTRUCTIVE = '#BA0E13';
export const CHIP_TEAL_BG = '#E7F1F1';
export const MONO = 'ui-monospace, Menlo, monospace';

export type Density = 'compact' | 'default' | 'comfortable';
export type SortDir = 'ASC' | 'DESC';

/** The page sizes the page-size picker offers. */
export const PAGE_SIZES = [25, 50, 100];

/* ------------------------------------------------------------------ */
/* Icons — traced from the approved mockup (20x20 viewBox, 1.6 stroke) */
/* ------------------------------------------------------------------ */

const PATHS: Record<string, React.ReactNode> = {
  search: (
    <>
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13.2 13.2L17 17" />
    </>
  ),
  close: <path d="M5 5l10 10M15 5L5 15" />,
  plus: <path d="M10 4v12M4 10h12" />,
  chev: <path d="M7 4.5l5.5 5.5L7 15.5" />,
  chevd: <path d="M4.5 7.5l5.5 5.5 5.5-5.5" />,
  sort: <path d="M6 4v12M6 16l-2.5-2.5M6 16l2.5-2.5M14 16V4M14 4l-2.5 2.5M14 4l2.5 2.5" />,
  columns: (
    <>
      <rect x="3" y="3.5" width="14" height="13" rx="1.5" />
      <path d="M8 3.5v13M12.5 3.5v13" />
    </>
  ),
  sliders: (
    <>
      <path d="M3 6h14M3 10h14M3 14h14" />
      <circle cx="7" cy="6" r="1.6" fill="#fff" />
      <circle cx="13" cy="10" r="1.6" fill="#fff" />
      <circle cx="8.5" cy="14" r="1.6" fill="#fff" />
    </>
  ),
  check: <path d="M4 10.5l4 4 8-8.5" />,
  kebab: (
    <>
      <circle cx="10" cy="4" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="16" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="10" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="10" cy="10" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  sync: (
    <>
      <path d="M16 10a6 6 0 0 1-10.4 4.1M4 10a6 6 0 0 1 10.4-4.1" />
      <path d="M14.5 2.5v3.5H11M5.5 17.5V14H9" />
    </>
  ),
  bell: (
    <>
      <path d="M5 14V9a5 5 0 0 1 10 0v5l1.5 1.5h-13z" />
      <path d="M8.5 17a1.5 1.5 0 0 0 3 0" />
    </>
  ),
  arrowd: <path d="M10 4v12M10 16l-4-4M10 16l4-4" />,
  inventory: (
    <>
      <path d="M3 7l7-4 7 4v9l-7 4-7-4z" />
      <path d="M3 7l7 4 7-4M10 11v9" />
    </>
  ),
  sales: (
    <>
      <path d="M5 3h10v14l-2-1.5L11 17l-2-1.5L7 17l-2-1.5z" />
      <path d="M7.5 7h5M7.5 10h5" />
    </>
  ),
};

interface IconProps {
  name: keyof typeof PATHS | string;
  color?: string;
  size?: number;
}

/** Mockup icon set. Stroke-based, inherits nothing so it renders identically inside the host shell. */
export const Icon: React.FC<IconProps> = ({ name, color = TEXT_DIM, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke={color}
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flex: 'none', color }}
  >
    {PATHS[name] ?? null}
  </svg>
);

/* ------------------------------------------------------------------ */
/* Small presentational pieces                                         */
/* ------------------------------------------------------------------ */

/** 18px selection box: outlined when off, teal-filled with a white check when on. */
export const SelectBox: React.FC<{ checked: boolean }> = ({ checked }) =>
  checked ? (
    <div
      style={{
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        backgroundColor: TEAL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      <Icon name="check" color="#FFFFFF" size={12} />
    </div>
  ) : (
    <div
      style={{
        width: '18px',
        height: '18px',
        borderRadius: '5px',
        border: `1.5px solid ${BORDER_STRONG}`,
        backgroundColor: '#FFFFFF',
        flex: 'none',
      }}
    />
  );

export type PillKind = 'ok' | 'off' | 'teal' | 'danger';

const PILL_COLORS: Record<PillKind, { bg: string; fg: string }> = {
  ok: { bg: '#E4F1EA', fg: '#1F6B4A' },
  off: { bg: '#F2F4F3', fg: '#606060' },
  teal: { bg: CHIP_TEAL_BG, fg: TEAL },
  danger: { bg: '#F6E0E0', fg: '#BA0E13' },
};

/** Status / Type pill. */
export const Pill: React.FC<{ label: string; kind: PillKind }> = ({ label, kind }) => {
  const c = PILL_COLORS[kind];
  return (
    <span
      className="font-inter"
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        backgroundColor: c.bg,
        color: c.fg,
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '18px',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

/** Small teal tag chip (sales attributes on Items, any short tag elsewhere). */
export const TagChip: React.FC<{ label: string }> = ({ label }) => (
  <span
    className="font-inter"
    style={{
      fontSize: '11.5px',
      color: TEAL,
      backgroundColor: CHIP_TEAL_BG,
      borderRadius: '999px',
      padding: '2px 9px',
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}
  >
    {label}
  </span>
);

/** Shared empty state block (desktop table body and mobile list). */
export interface ListEmptyState {
  title: string;
  desc: string;
  img?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/* Money and dates                                                     */
/* ------------------------------------------------------------------ */

/**
 * The symbol shown next to every price in a list. There is no
 * company-currency field anywhere in the stack yet; EUR is the product's
 * convention (SalesOverview.tsx hard-codes `currency: 'EUR'` the same way).
 * When a company currency lands, this is the one place for it to plug in.
 */
export const CURRENCY_SYMBOL = '€';

/** "€ 11.50" — tabular figures are applied by the caller. Em-dash when unpriced. */
export const formatMoney = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? '—'
    : `${CURRENCY_SYMBOL} ${Number(value).toFixed(2)}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "14 Aug" (current year) or "14 Aug 2025". */
export const formatDayMonth = (value: any): string => {
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const stem = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? stem : `${stem} ${d.getFullYear()}`;
};

/**
 * The "Last updated" column: "Today 10:14", "Yesterday 16:20", "18 Aug 08:50",
 * "18 Aug 2025" for anything older than the current year.
 */
export const formatUpdatedAt = (value: any, language: any): string => {
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return '';
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const now = new Date();
  if (isSameDay(d, now)) return `${t(language, 'Today', 'Today')} ${hhmm}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return `${t(language, 'Yesterday', 'Yesterday')} ${hhmm}`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${hhmm}`;
  }
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/** dd.MM.yyyy — the format the API's `searchTime` filters parse. */
export const toSearchTime = (date: Date): string =>
  `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;

/** Parse a dd.MM.yyyy `searchTime` back into a Date for the picker. */
export const fromSearchTime = (value: string): Date | null => {
  const parts = String(value ?? '').split('.');
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The local calendar day as YYYY-MM-DD (never toISOString(), which shifts +UTC users back a day). */
export const toLocalDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/* ------------------------------------------------------------------ */
/* Contracts                                                           */
/* ------------------------------------------------------------------ */

/** Where a column lands on the mobile card. Columns without a role go to the record sheet. */
export type MobileRole = 'title' | 'subtitle' | 'trailing' | 'hidden' | 'sheet';

/** What a cell renderer gets besides the row. */
export interface CellContext {
  language: any;
  /** The active sort (empty when the list is not sortable). */
  sortBy: string;
  sortDir: SortDir;
  /** Index of the row on the current page, plus the page itself. */
  index: number;
  page: number;
  limit: number;
  column: ListColumn;
}

export type CellRenderer<Row = any> = (row: Row, ctx: CellContext) => React.ReactNode;

export interface ListColumn<Row = any> {
  key: string;
  /** i18n key of the header label (the English string). */
  label: string;
  /**
   * Desktop cell width in px. Columns keep their width while they fit and
   * shrink (with an ellipsis) down to `minWidth` before the grid scrolls
   * sideways; the kebab column stays pinned to the right edge.
   */
  width: number;
  /** Narrowest the column shrinks to. Defaults to `min(width, 72)`. */
  minWidth?: number;
  /** Only where the endpoint accepts sortBy/sortDir. Headers of non-sortable columns are plain. */
  sortable?: boolean;
  /** The sortBy value sent for this column (defaults to `key`). */
  sortKey?: string;
  /** Raw value for the default text cell and for the mobile subtitle / sheet rows. */
  value?: (row: Row, ctx: CellContext) => any;
  /** Desktop cell. Defaults to a plain text cell over `value`. */
  render?: CellRenderer<Row>;
  /** Mobile card / sheet cell when it differs from the desktop one. Defaults to `render`. */
  mobileRender?: CellRenderer<Row>;
  align?: 'left' | 'right';
  mobile?: MobileRole;
  /** Listed in the Columns popover (default true). The first column should pass false. */
  hideable?: boolean;
  /** Render on the desktop table (default true). Mobile-only helper columns pass false. */
  desktop?: boolean;
  /** Clicking the desktop cell (typically the title column opening the record). */
  onClick?: (row: Row) => void;
}

export interface ListFilterOption {
  value: string;
  /** Already translated. */
  label: string;
}

export interface ListFilter {
  key: string;
  /** i18n key. */
  label: string;
  /**
   * 'text' is a free-text filter (committed on Enter / Apply) for endpoints
   * that take per-field search params (searchDevice, searchDescription, ...)
   * instead of one unified search; the typed value is sent as the filter value.
   */
  kind: 'select' | 'date' | 'text';
  options?: ListFilterOption[];
  /** Chip / sheet text for a value. Defaults to the option label, or the raw value. */
  valueLabel?: (value: string, language: any) => string;
}

export interface ListSortOption {
  value: string;
  /** i18n key. */
  label: string;
}

/**
 * Direction labels for a sort field. Dates read Newest/Oldest; anything else
 * (name, SKU, amount…) reads Ascending/Descending — "Newest first" on a Name
 * sort is meaningless. Heuristic on the field key; pass `dateLike` to force it.
 */
export const sortDirLabelOf = (
  language: any,
  dir: SortDir,
  field: string,
  dateLike?: boolean
): string => {
  const isDate = dateLike ?? /date|time|created|updated|at$/i.test(field);
  if (isDate) {
    return dir === 'DESC'
      ? t(language, 'Newest first', 'Newest first')
      : t(language, 'Oldest first', 'Oldest first');
  }
  return dir === 'DESC'
    ? t(language, 'Descending', 'Descending')
    : t(language, 'Ascending', 'Ascending');
};

export interface ListBulkAction {
  key: string;
  /** i18n key. */
  label: string;
  onRun: (selectedIds: string[]) => void;
}

export interface ListRowAction<Row = any> {
  key: string;
  /** i18n key, or a function of the row (already translated). */
  label: string | ((row: Row) => string);
  /** Desktop kebab-menu icon (an SVG component instance). */
  icon?: React.ReactNode;
  /** Mobile sheet icon (a name from the kit's Icon set). */
  sheetIcon?: string;
  /** Mobile sheet subline (i18n key). */
  sub?: string;
  danger?: boolean;
  /** Hide this action for rows it doesn't apply to. */
  hidden?: (row: Row) => boolean;
  onRun: (row: Row) => void;
}

/** The page-level query the kit hands to a screen's fetcher. */
export interface ListQuery {
  page: number;
  limit: number;
  search: string;
  filters: Record<string, string>;
  sortBy: string;
  sortDir: SortDir;
}

/** Chip / sheet text for a filter value. */
export const filterValueLabel = (filter: ListFilter, value: string, language: any): string => {
  if (!value) return t(language, 'Any', 'Any');
  if (filter.valueLabel) return filter.valueLabel(value, language);
  const option = filter.options?.find(o => o.value === value);
  return option ? option.label : value;
};

/* ------------------------------------------------------------------ */
/* Built-in cell renderers                                             */
/* ------------------------------------------------------------------ */

const ELLIPSIS: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

type Getter<Row, V> = (row: Row, ctx: CellContext) => V;

/** Plain 14px text, single line with an ellipsis. */
const text =
  <Row,>(get: Getter<Row, any>): CellRenderer<Row> =>
  (row, ctx) => (
    <div className="font-inter" style={{ fontSize: '14px', color: TEXT, ...ELLIPSIS }}>
      {get(row, ctx)}
    </div>
  );

/** Primary line over a muted monospace secondary line (name over SKU / code). */
const primary =
  <Row,>(getPrimary: Getter<Row, any>, getSecondary?: Getter<Row, any>): CellRenderer<Row> =>
  (row, ctx) => (
    <div className="flex flex-col" style={{ minWidth: 0 }}>
      <span className="font-inter" style={{ fontSize: '14px', fontWeight: 500, color: TEXT, ...ELLIPSIS }}>
        {getPrimary(row, ctx)}
      </span>
      {getSecondary && (
        <span style={{ fontSize: '12px', color: TEXT_FAINT, fontFamily: MONO, ...ELLIPSIS }}>
          {getSecondary(row, ctx)}
        </span>
      )}
    </div>
  );

/** Green / grey status pill. Labels are i18n keys. */
const status =
  <Row,>(
    isOn: Getter<Row, boolean>,
    labels: { on: string; off: string } = { on: 'Active', off: 'Inactive' }
  ): CellRenderer<Row> =>
  (row, ctx) => {
    const on = !!isOn(row, ctx);
    return (
      <Pill
        label={on ? t(ctx.language, labels.on, labels.on) : t(ctx.language, labels.off, labels.off)}
        kind={on ? 'ok' : 'off'}
      />
    );
  };

/** Teal type pill; `getLabel` returns the translated label. */
const typePill =
  <Row,>(getLabel: Getter<Row, string>): CellRenderer<Row> =>
  (row, ctx) => <Pill label={getLabel(row, ctx)} kind="teal" />;

/** "€ 11.50" with tabular figures. */
const money =
  <Row,>(get: Getter<Row, number | null | undefined>, strong = false): CellRenderer<Row> =>
  (row, ctx) => (
    <div
      className="font-inter"
      style={{
        fontSize: '14px',
        fontWeight: strong ? 500 : undefined,
        color: TEXT,
        fontVariantNumeric: 'tabular-nums',
        ...ELLIPSIS,
      }}
    >
      {formatMoney(get(row, ctx))}
    </div>
  );

/**
 * "Today 10:14" / "18 Aug 08:50" / "18 Mar 2025". Turns teal while the list is
 * sorted by this column.
 */
const date =
  <Row,>(get: Getter<Row, any>): CellRenderer<Row> =>
  (row, ctx) => {
    const sortKey = ctx.column.sortKey ?? ctx.column.key;
    const highlighted = !!ctx.sortBy && ctx.sortBy === sortKey;
    return (
      <div
        className="font-inter"
        style={{ fontSize: '13px', color: highlighted ? TEAL : TEXT_MUTED, ...ELLIPSIS }}
      >
        {formatUpdatedAt(get(row, ctx), ctx.language)}
      </div>
    );
  };

/** Up to `max` tag chips and a "+N" overflow. */
const chips =
  <Row,>(get: Getter<Row, string[]>, max = 2): CellRenderer<Row> =>
  (row, ctx) => {
    const names = get(row, ctx) ?? [];
    return (
      <div className="flex items-center" style={{ gap: '4px', overflow: 'hidden' }}>
        {names.slice(0, max).map((name: string, index: number) => (
          <TagChip key={`${ctx.column.key}-chip-${index}`} label={name} />
        ))}
        {names.length > max && (
          <span style={{ fontSize: '11.5px', color: TEXT_DIM }}>+{names.length - max}</span>
        )}
      </div>
    );
  };

/**
 * On / off switch (additive) — the `switch` / `slider` classes from the host
 * shell's index.css, the same markup TableReportV2 used for Status toggles.
 * Stops propagation so a tap on a mobile card does not also open the record.
 */
const toggle =
  <Row,>(isOn: Getter<Row, boolean>, onToggle: (row: Row) => void): CellRenderer<Row> =>
  (row, ctx) => {
    const on = !!isOn(row, ctx);
    return (
      <div className="flex items-center" style={{ height: '24px' }}>
        <div
          className={`switch ${on ? 'on' : 'off'}`}
          onClick={e => {
            e.stopPropagation();
            onToggle(row);
          }}
        >
          <div className={`shadow_toggle slider ${on ? 'move-right' : 'move-left'}`} />
        </div>
      </div>
    );
  };

export const cells = { text, primary, status, typePill, money, date, chips, toggle };

/**
 * A cell's content: on mobile the `mobileRender` override first, then the
 * desktop renderer, then a plain text cell over `value`.
 */
export const cellValue = (
  column: ListColumn,
  row: any,
  ctx: CellContext,
  surface: 'desktop' | 'mobile' = 'mobile'
): React.ReactNode => {
  if (surface === 'mobile' && column.mobileRender) return column.mobileRender(row, ctx);
  if (column.render) return column.render(row, ctx);
  return text(() => cellText(column, row, ctx))(row, ctx);
};

/** Plain string for a column (subtitle joins): `value` first, raw field second. */
export const cellText = (column: ListColumn, row: any, ctx: CellContext): string => {
  const value = column.value ? column.value(row, ctx) : row?.[column.key];
  return value === null || value === undefined ? '' : String(value);
};

/* ------------------------------------------------------------------ */
/* Bottom sheet                                                        */
/* ------------------------------------------------------------------ */

const BACKDROP: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(34,37,37,.35)',
  zIndex: 190,
};

const PANEL: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  borderRadius: '22px 22px 0 0',
  // Keeps the last action above the iPhone home indicator.
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
  boxShadow: '0 -12px 40px rgba(0,0,0,.12)',
  zIndex: 200,
  maxHeight: '88vh',
  overflowY: 'auto',
};

/**
 * Open sheets, bottom-most first. Only the top of the stack handles Escape,
 * and the body scroll lock is released when the last sheet closes.
 */
const openSheets: symbol[] = [];
let bodyOverflowBeforeLock: string | null = null;

const lockBodyScroll = (token: symbol) => {
  if (openSheets.length === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  openSheets.push(token);
};

const unlockBodyScroll = (token: symbol) => {
  const index = openSheets.indexOf(token);
  if (index >= 0) openSheets.splice(index, 1);
  if (openSheets.length === 0 && bodyOverflowBeforeLock !== null) {
    document.body.style.overflow = bodyOverflowBeforeLock;
    bodyOverflowBeforeLock = null;
  }
};

/**
 * Rounded bottom sheet with the mockup's grabber. A modal dialog: focus moves
 * into the panel on open and back to the opener on close, the body does not
 * scroll behind it, and Escape closes the top-most sheet. `zIndex` lets a
 * stacked sheet (e.g. a confirm) sit above the one that opened it.
 */
export const BottomSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Panel z-index (backdrop sits 10 below). Default 200. */
  zIndex?: number;
}> = ({ open, onClose, title, children, zIndex = 200 }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  React.useEffect(() => {
    if (!open) return undefined;
    const token = Symbol('bottom-sheet');
    const opener = document.activeElement as HTMLElement | null;
    lockBodyScroll(token);

    panelRef.current?.focus({ preventScroll: true });

    const FOCUSABLE =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const onKeyDown = (e: KeyboardEvent) => {
      if (openSheets[openSheets.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      // aria-modal promises the page beneath is inert: keep Tab inside the panel.
      if (e.key === 'Tab' && panelRef.current) {
        const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          n => n.offsetParent !== null
        );
        if (nodes.length === 0) {
          e.preventDefault();
          panelRef.current.focus();
          return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement as HTMLElement | null;
        const inside = active ? panelRef.current.contains(active) : false;
        if (e.shiftKey && (active === first || !inside)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !inside)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      unlockBodyScroll(token);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div style={{ ...BACKDROP, zIndex: zIndex - 10 }} onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{ ...PANEL, zIndex, overscrollBehavior: 'contain', outline: 'none' }}
      >
        <div className="flex justify-center" style={{ padding: '10px 0 6px' }}>
          <div
            style={{ width: '40px', height: '4px', borderRadius: '999px', backgroundColor: '#DBE4E4' }}
          />
        </div>
        {title && (
          <div
            className="font-inter"
            style={{ padding: '4px 20px 12px', fontSize: '18px', fontWeight: 600, color: TEXT }}
          >
            {title}
          </div>
        )}
        {children}
      </div>
    </>
  );
};

/** One tappable row inside a bottom sheet: icon, label and optional subline. */
export const SheetRow: React.FC<{
  icon: string;
  label: string;
  sub?: string;
  danger?: boolean;
  onClick: () => void;
}> = ({ icon, label, sub, danger, onClick }) => (
  <div
    className="flex items-center cursor-pointer"
    style={{ gap: '14px', minHeight: '52px', padding: '8px 20px' }}
    onClick={onClick}
  >
    <Icon name={icon} color={danger ? DESTRUCTIVE : TEXT_MUTED} size={20} />
    <div style={{ flex: 1 }}>
      <div
        className="font-inter"
        style={{ fontSize: '15px', fontWeight: 500, color: danger ? DESTRUCTIVE : TEXT }}
      >
        {label}
      </div>
      {sub && (
        <div className="font-inter" style={{ fontSize: '12.5px', color: TEXT_DIM, marginTop: '1px' }}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

/** Shared empty-state block. */
export const EmptyState: React.FC<{ state: ListEmptyState; fallbackImg: React.ReactNode }> = ({
  state,
  fallbackImg,
}) => (
  <div
    className="flex justify-center items-center"
    style={{
      flexDirection: 'column',
      backgroundColor: '#F8F8F8',
      borderRadius: '16px',
      padding: '24px',
    }}
  >
    <div className="flex items-center justify-center" style={{ width: '48px', height: '48px' }}>
      {state.img ?? fallbackImg}
    </div>
    <div className="space-y-1" style={{ marginTop: '12px', textAlign: 'center' }}>
      <p
        className="font-inter"
        style={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: TEXT }}
      >
        {state.title}
      </p>
      <p
        className="font-inter"
        style={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: TEXT_DIM }}
      >
        {state.desc}
      </p>
    </div>
  </div>
);

/**
 * Shown instead of the entity's empty state when a search or filter is
 * active and returned nothing. The three strings are the keys the legacy
 * TableReportV2 used, so the existing translations keep working.
 */
export const NoResults: React.FC<{ language: any }> = ({ language }) => (
  <div
    className="flex justify-center items-center"
    style={{
      flexDirection: 'column',
      backgroundColor: '#F8F8F8',
      borderRadius: '16px',
      padding: '24px',
    }}
  >
    <div className="space-y-1" style={{ marginTop: '16px', textAlign: 'center' }}>
      <p
        className="font-inter"
        style={{ fontSize: '16px', fontWeight: 500, lineHeight: '20px', color: TEXT }}
      >
        {t(language, 'No Results Found', 'No Results Found')}
      </p>
      <p
        className="font-inter"
        style={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: TEXT_MUTED }}
      >
        {t(language, 'We couldn’t find anything matching ', 'We couldn’t find anything matching ')}
      </p>
      <p
        className="font-inter"
        style={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: TEXT_MUTED }}
      >
        {t(language, 'your search. ', 'your search. ')}
      </p>
    </div>
  </div>
);
